import { AutoPayFormData } from '../components/modals/AutoPayAuthorizationModal';

import { formatInET, getETZoneAbbr } from './helper';

/**
 * Get the brand logo path for the PDF
 * Uses the brand's custom logo if available, otherwise defaults to AuxcapLogo.svg
 * @param brandLogo - Brand logo path from Redux store
 * @returns Logo path to use in PDF
 */
const getBrandLogoPath = (brandLogo?: string | null): string => {
    if (brandLogo) {
        // If logo starts with http/https, it's an absolute URL
        if (brandLogo.startsWith('http://') || brandLogo.startsWith('https://')) {
            return brandLogo;
        }
        // Otherwise it's a relative path, prepend with profile URL base if needed
        const profileUrlBase = process.env.REACT_APP_PROFILE_URL || '';
        return `${profileUrlBase}${brandLogo}`;
    }
    // Fallback to default AuxcapLogo.svg
    return '/AuxcapLogo.svg';
};

const getUserAddress = (...data: (string | null | undefined)[]) => {
    return [...data.filter(Boolean)].join(', ');
};

const formattedPhoneNumber = (phoneNumber: string) => {
    if (!phoneNumber) return 'N/A';
    return phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
};

/**
 * Remove masking from bank account numbers for PDF display
 * If the number is masked (contains asterisks), extract the visible digits
 * If it's already unmasked, return as-is
 * @param maskedValue - The potentially masked value (e.g., "****1234" or "12341234567")
 * @returns The unmasked or original value
 */
const unmaskBankAccountNumber = (maskedValue: string): string => {
    if (!maskedValue) return '';

    // If value contains asterisks, it's masked - extract just the digits
    if (maskedValue.includes('*')) {
        // Remove all asterisks and special characters, keep only digits
        return maskedValue.replace(/[^0-9]/g, '');
    }

    // Value is already unmasked or has no masking applied
    return maskedValue;
};

/**
 * Generates dynamic HTML content for AutoPay authorization PDF
 * @param formData - Form data containing borrower name, form type, and bank account details
 * @param agreementNumber - Agreement/Contract number
 * @param userDetails - User details from Redux store
 * @param brandDetails - Brand details for custom logo rendering (optional)
 * @returns Promise<string> - HTML content as string
 */
export const generateAutoPayHTML = async (
    formData: AutoPayFormData,
    agreementNumber?: string,
    userDetails?: Record<string, unknown>,
    brandDetails?: Record<string, unknown>,
): Promise<string> => {
    // Determine which HTML template to use based on formType
    // HTML templates should be in public/autopay-pdfs/ folder
    const templatePath = `/autopay-pdfs/${formData.formType}.html`;
    try {
        // Fetch the HTML template from public folder
        const response = await fetch(templatePath);
        if (!response.ok) {
            throw new Error(`Failed to load template: ${response.statusText}`);
        }
        let htmlContent = await response.text();

        // Get bank account details
        const bankAccount = formData.bankAccount;
        if (!bankAccount) {
            throw new Error('Bank account details are required');
        }

        // Format account type (capitalize first letter and add "Account" suffix)
        const rawAccountType = String(
            (bankAccount as unknown as Record<string, unknown>).AccountType ?? bankAccount.accountType ?? '',
        );
        const accountTypeLower = rawAccountType.toLowerCase();
        let accountType = '';
        if (accountTypeLower === 'checking') {
            accountType = 'Checking Account';
        } else if (accountTypeLower === 'savings') {
            accountType = 'Savings Account';
        } else if (rawAccountType) {
            accountType = rawAccountType.charAt(0).toUpperCase() + rawAccountType.slice(1).toLowerCase() + ' Account';
        }

        // Current date and time in Eastern Time (EST/EDT), independent of the browser timezone
        const now = new Date();
        const dateStr = formatInET(now);
        const timeStr = formatInET(now, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
        const timeZoneAbbr = getETZoneAbbr(now);

        // Format time string to match HTML format: "2:30:45 PM EST"
        const formattedTime = `${timeStr} ${timeZoneAbbr}`;

        // Get user details for company information
        const ud = userDetails ?? {};
        const legalName = String(ud.Name ?? '');
        const fedTaxId = String(ud.SSN ?? 'N/A');
        const legalAddress =
            getUserAddress(
                String(ud.Street ?? ''),
                String(ud.City ?? ''),
                String(ud.State ?? ''),
                String(ud.Country ?? ''),
                String(ud.PostalCode ?? ''),
            ) || 'N/A';
        const phoneNumbers = ud.phoneNumbers as Array<{ IsPrimary: boolean; PhoneNumber: string }> | undefined;
        const phoneNumber =
            formattedPhoneNumber(phoneNumbers?.[0]?.IsPrimary === true ? phoneNumbers?.[0]?.PhoneNumber : 'N/A') ||
            'N/A';
        const emailAddress = String(ud.Email ?? ud.EmailAddress ?? '');

        // Determine Tax ID label based on country and form type
        const country = String(ud.Country ?? '');
        const isUSA = country === 'USA';
        const isCanada = country === 'CANADA';
        const isConsumer = formData.formType.includes('consumer');

        let taxIdLabel = 'Fed Tax ID:';
        if (isUSA) {
            taxIdLabel = isConsumer ? 'SSN Number:' : 'Fed Tax Number:';
        } else if (isCanada) {
            taxIdLabel = isConsumer ? 'SIN:' : 'Business Number:';
        }

        // Get dynamic brand logo path
        const brandLogoPath = getBrandLogoPath(brandDetails?.Logo as string | null | undefined);

        // Format timestamp for signature: "MM/DD/YYYY at HH:MM:SS AM/PM TZ" (Eastern Time)
        const signatureTimestamp = `${dateStr} at ${timeStr} ${timeZoneAbbr}`;

        // Replace placeholders in HTML (using global replace for all occurrences)
        htmlContent = htmlContent
            // Logo path - use brand logo dynamically
            .replace(/AuxcapLogo\.png/g, brandLogoPath)
            // Date timestamp for signature - format: "DD MMM YYYY, HH:MM AM/PM"
            .replace(/AUTOPAY_PDF_TIMESTAMP/g, signatureTimestamp)
            // Contract/Agreement number
            .replace(/C14143-003/g, agreementNumber || 'N/A')
            // Document Generated date and time - replace all occurrences
            .replace(/12\/05\/2025/g, dateStr)
            .replace(/2:30:45 PM EST/g, formattedTime)
            .replace(/2:30:45 PM/g, timeStr) // Fallback for time without timezone
            // Company/User details - Lender Name (FirstName + LastName)
            .replace(/PIRANHA RENTS, LLC/g, legalName)
            // Tax ID label (dynamic based on country and type)
            .replace(/Fed Tax ID:/g, taxIdLabel)
            // Fed Tax ID
            .replace(/86-3805030/g, fedTaxId)
            // Lender Address
            .replace(/3375 Atlantic Ave Penfield, NY 14526/g, legalAddress)
            // Phone Number
            .replace(/\(585\) 506-2005/g, phoneNumber)
            // Email Address
            .replace(/patrick@compactproequipment\.com/g, emailAddress)
            // Bank details - replace all occurrences
            .replace(/Bank of America/g, bankAccount.BankName || '')
            .replace(/123456789/g, unmaskBankAccountNumber(bankAccount.IdentificationNumber || ''))
            .replace(/12341234567/g, unmaskBankAccountNumber(bankAccount.AccountNumber || ''))
            // Account type - handle both "Savings Account" and "Checking Account"
            .replace(/Savings Account/g, accountType)
            .replace(/Checking Account/g, accountType)
            // Borrower name - replace all occurrences
            .replace(/Academy Bus LLC/g, formData.borrower || '')
            // Signature
            .replace(/Manoj Kumar/g, formData.borrower || '');

        return htmlContent;
    } catch (error) {
        console.error('Error generating AutoPay HTML:', error);
        throw error;
    }
};

/**
 * Converts HTML content to PDF blob
 * @param htmlContent - HTML content string
 * @returns Promise<Blob> - PDF blob
 */
export const convertHTMLToPDF = async (htmlContent: string): Promise<Blob> => {
    // Dynamically import html2pdf.js
    const html2pdf = (await import('html2pdf.js')).default;

    // Parse HTML to extract styles and body content
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');

    // Extract styles from head
    const styles = Array.from(doc.querySelectorAll('style'))
        .map(style => style.innerHTML)
        .join('\n');

    // Extract body content
    const bodyContent = doc.body.innerHTML;

    // Create a wrapper div that will contain everything
    const wrapper = document.createElement('div');
    wrapper.id = 'pdf-generation-wrapper';

    // Create a style element and inject styles into the document head
    const styleElement = document.createElement('style');
    styleElement.id = 'pdf-generation-styles';
    styleElement.textContent = styles;
    document.head.appendChild(styleElement);

    // Create a container div with extracted content
    const container = document.createElement('div');
    container.innerHTML = bodyContent;

    // Style the wrapper to be visible in viewport but behind everything
    // html2canvas needs elements to be in the viewport to capture them
    wrapper.style.position = 'fixed';
    wrapper.style.left = '0';
    wrapper.style.top = '0';
    wrapper.style.width = '800px';
    wrapper.style.backgroundColor = '#ffffff';
    wrapper.style.visibility = 'visible';
    wrapper.style.display = 'block';
    wrapper.style.zIndex = '-1';
    wrapper.style.opacity = '0.01'; // Nearly invisible but still rendered
    wrapper.style.pointerEvents = 'none'; // Don't interfere with user interaction

    container.style.width = '100%';
    container.style.backgroundColor = '#ffffff';
    container.style.padding = '0';
    container.style.margin = '0';

    wrapper.appendChild(container);
    document.body.appendChild(wrapper);

    try {
        // Wait for styles to apply
        await new Promise(resolve => setTimeout(resolve, 500));

        // Wait for all images to load
        const images = container.querySelectorAll('img');
        const imagePromises = Array.from(images).map(img => {
            if (img.complete) return Promise.resolve();
            return new Promise<void>(resolve => {
                img.onload = () => {
                    resolve();
                };
                img.onerror = () => {
                    resolve();
                };
                setTimeout(() => resolve(), 5000);
            });
        });
        await Promise.all(imagePromises);

        // Wait for fonts and styles to fully apply
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Check if container has content
        const hasContent = container.innerHTML.trim().length > 0;
        if (!hasContent) {
            throw new Error('Container appears to be empty - innerHTML is empty');
        }

        // Force a reflow to ensure rendering
        void container.offsetHeight;

        // Wait a bit more for rendering
        await new Promise(resolve => setTimeout(resolve, 500));

        // Check again after reflow
        if (container.scrollHeight === 0 && container.offsetHeight === 0) {
            console.warn('Container has no height after rendering - this might cause issues');
        }

        // Configure html2pdf options - use wrapper instead of container
        const opt = {
            margin: [0.5, 0.5, 0.5, 0.5] as [number, number, number, number],
            filename: 'autopay-authorization.pdf',
            image: { type: 'jpeg' as const, quality: 0.98 },
            html2canvas: {
                scale: 2,
                useCORS: true,
                logging: true, // Enable logging to debug
                letterRendering: true,
                backgroundColor: '#ffffff',
                windowWidth: 800,
                windowHeight: Math.max(wrapper.scrollHeight || container.scrollHeight, 1200),
                allowTaint: true,
            },
            jsPDF: { unit: 'in' as const, format: 'letter' as const, orientation: 'portrait' as const },
        };

        // Generate PDF blob - use container (the inner div with content)
        const pdfBlob = await html2pdf().set(opt).from(container).outputPdf('blob');

        // Verify blob is valid
        if (!pdfBlob || pdfBlob.size === 0) {
            throw new Error('Generated PDF blob is empty');
        }

        // Clean up
        if (document.body.contains(wrapper)) {
            document.body.removeChild(wrapper);
        }
        const styleEl = document.getElementById('pdf-generation-styles');
        if (styleEl) {
            document.head.removeChild(styleEl);
        }

        return pdfBlob;
    } catch (error) {
        console.error('Error converting HTML to PDF:', error);
        // Clean up on error
        const wrapperEl = document.getElementById('pdf-generation-wrapper');
        if (wrapperEl && document.body.contains(wrapperEl)) {
            document.body.removeChild(wrapperEl);
        }
        const styleEl = document.getElementById('pdf-generation-styles');
        if (styleEl) {
            document.head.removeChild(styleEl);
        }
        throw error;
    }
};

/**
 * Creates a data URL from PDF blob for preview
 * @param pdfBlob - PDF blob
 * @returns string - Data URL
 */
export const createPDFPreviewURL = (pdfBlob: Blob): string => {
    return URL.createObjectURL(pdfBlob);
};

/**
 * Converts PDF blob to File object
 * @param pdfBlob - PDF blob
 * @param filename - Optional filename (default: 'autopay-authorization.pdf')
 * @returns File object
 */
export const convertBlobToFile = (pdfBlob: Blob, filename: string = 'autopay-authorization.pdf'): File => {
    return new File([pdfBlob], filename, { type: 'application/pdf' });
};

/**
 * Converts PDF blob to base64 string
 * @param pdfBlob - PDF blob
 * @returns Promise<string> - Base64 encoded string
 */
export const convertBlobToBase64 = (pdfBlob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64String = reader.result as string;
            // Remove data URL prefix if present
            const base64 = base64String.includes(',') ? base64String.split(',')[1] : base64String;
            resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(pdfBlob);
    });
};
